'use client';

/**
 * Landing D — "SaaS Landing Page Strategist" pattern
 *
 * Design philosophy: conversion-first structure over narrative/premium styling.
 * Single sharp positioning statement, named ICP, pain → mechanism → proof → offer.
 * No animation-for-animation's-sake; every section exists to answer one buyer
 * objection in sequence (What is it? For me? Does it work? What does it cost?).
 *
 * Promoted to the main homepage (/) — rendered by app/page.tsx with the real
 * SEO metadata (homeMetadata) and site-wide analytics tag (both live in the
 * root layout/page already). The previous homepage is preserved, unchanged,
 * at /landing-classic. /landing-d now redirects here to avoid duplicate content.
 */

import { useState } from 'react';
import Link from 'next/link';
import LogoAnimatedGreeks from '@/components/logos/LogoAnimatedGreeks';
import GlobalTraderMap from '@/components/GlobalTraderMap';
import { STATIC_PLANS, type StaticPlan as Plan } from '@/lib/static-plans';

const IconCheck = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const IconX = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M18 6 6 18" /><path d="m6 6 12 12" />
  </svg>
);
const IconArrowRight = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
  </svg>
);
const IconGauge = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="m12 14 4-4" /><path d="M3.34 19a10 10 0 1 1 17.32 0" />
  </svg>
);
const IconMagnet = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M6 15v-2a6 6 0 0 1 12 0v2" /><path d="M18 15a3 3 0 1 1-6 0v-2a3 3 0 1 0-6 0v2" /><path d="M6 15h2" /><path d="M16 15h2" />
  </svg>
);
const IconLayers = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z" /><path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65" /><path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65" />
  </svg>
);
const IconWaves = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" /><path d="M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" /><path d="M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
  </svg>
);
const IconEye = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" />
  </svg>
);
const IconChart = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M3 3v18h18" /><path d="m19 9-5 5-4-4-3 3" />
  </svg>
);
const IconZap = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);
const IconActivity = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);
const IconServer = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect width="20" height="8" x="2" y="2" rx="2" ry="2" /><rect width="20" height="8" x="2" y="14" rx="2" ry="2" /><line x1="6" x2="6.01" y1="6" y2="6" /><line x1="6" x2="6.01" y1="18" y2="18" />
  </svg>
);

// Illustrative GEX-by-strike snapshot for the hero — static SVG, labeled as a
// sample, not live data. Mirrors the product's real chart language (diverging
// bars around a zero axis, spot marker, flip line) rather than a generic chart.
function HeroGexSnapshot() {
  // Synthetic gamma-exposure curve: negative (dealer short gamma) below strikes
  // near spot, positive (dealer long gamma) above — shape only, not real data.
  const raw = [
    -8, -14, -22, -34, -52, -74, -98, -118, -104, -80,
    -54, -30, -14, 22, 58, 96, 132, 108, 78, 54, 36, 22, 14, 8,
  ];
  const flipIndex = 13; // first positive bar
  const spotIndex = 10; // a few strikes left of flip, like the real chart — kept apart so labels don't collide

  // Same palette as the live Signal Engine GEX/VANX panel: teal for dealer
  // long gamma, red for short gamma.
  const TEAL = '#2dd4bf';
  const RED = '#f43f5e';
  const LINE = '#fb7185';

  const W = 760, H = 260;
  const padL = 44, padR = 16, padT = 34, padB = 34;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const zeroY = padT + plotH * 0.58;
  const maxAbs = Math.max(...raw.map(Math.abs));
  const n = raw.length;
  const gap = 3;
  const barW = (plotW - gap * (n - 1)) / n;
  const barTopY = (v: number) => {
    const h = (Math.abs(v) / maxAbs) * (plotH * 0.42);
    return v >= 0 ? zeroY - h : zeroY + h;
  };

  const xAt = (i: number) => padL + i * (barW + gap);
  const spotX = xAt(spotIndex) + barW / 2;
  const flipX = xAt(flipIndex) - gap / 2;
  const linePath = raw.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i) + barW / 2} ${barTopY(v)}`).join(' ');

  // Strike labels along the x-axis, same cadence as the real chart (every 20pts)
  const strikeLabels = ['7480', '7500', '7520', '7540', '7560', '7580', '7600', '7620', '7640', '7660', '7680', '7700', '7720', '7740', '7760', '7780'];

  const legend: { label: string; color: string }[] = [
    { label: 'SPOT', color: '#facc15' },
    { label: 'FLIP', color: '#2dd4bf' },
    { label: 'CW', color: '#22d3ee' },
    { label: 'PW', color: '#f43f5e' },
  ];

  return (
    <div className="max-w-3xl mx-auto mt-10 bg-[#0a0e17] border border-white/15 rounded-2xl p-5 md:p-6 shadow-xl shadow-black/40">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: '#22d3ee' }} />
        <p className="text-xs font-mono uppercase tracking-widest text-gray-300 font-semibold">GEX / VANX</p>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 mb-4">
        {legend.map((l) => (
          <span key={l.label} className="flex items-center gap-1.5 bg-white/[0.06] border border-white/10 rounded px-2 py-1 text-[9px] font-mono font-bold uppercase tracking-wide text-gray-200">
            <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: l.color }} />{l.label}
          </span>
        ))}
        <span className="ml-auto text-[9px] font-mono uppercase tracking-wide text-gray-600 border border-gray-700 rounded px-1.5 py-0.5">Sample data, illustrative</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Sample gamma exposure and vanna exposure chart by strike, diverging above and below zero">
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} x1={padL} x2={W - padR} y1={padT + plotH * f} y2={padT + plotH * f} stroke="white" strokeOpacity="0.06" strokeDasharray="2 3" strokeWidth="1" />
        ))}
        <line x1={padL} x2={W - padR} y1={zeroY} y2={zeroY} stroke="white" strokeOpacity="0.22" strokeWidth="1" />

        {/* Dual y-axis corner labels — GEX scale (left), VANX scale (right), like the live panel */}
        <text x={padL} y={padT - 12} fill="#7dd3fc" fontSize="10" fontFamily="ui-monospace, monospace">2B</text>
        <text x={padL} y={H - padB + 22} fill="#7dd3fc" fontSize="10" fontFamily="ui-monospace, monospace">-2B</text>
        <text x={W - padR} y={padT - 12} textAnchor="end" fill="#7dd3fc" fontSize="10" fontFamily="ui-monospace, monospace">97M</text>
        <text x={W - padR} y={H - padB + 22} textAnchor="end" fill="#7dd3fc" fontSize="10" fontFamily="ui-monospace, monospace">-97M</text>

        <line x1={flipX} x2={flipX} y1={padT} y2={H - padB} stroke="#22d3ee" strokeOpacity="0.7" strokeDasharray="1.5 3" strokeWidth="1.25" />
        <line x1={spotX} x2={spotX} y1={padT} y2={H - padB} stroke="#facc15" strokeOpacity="0.85" strokeDasharray="4 3" strokeWidth="1.25" />

        {raw.map((v, i) => {
          const h = (Math.abs(v) / maxAbs) * (plotH * 0.42);
          const x = xAt(i);
          const y = v >= 0 ? zeroY - h : zeroY;
          return (
            <rect
              key={i}
              x={x} y={y} width={barW} height={Math.max(h, 1.5)}
              rx="1"
              fill={v >= 0 ? TEAL : RED}
              opacity="0.88"
            />
          );
        })}

        <path d={linePath} fill="none" stroke={LINE} strokeWidth="1.5" strokeOpacity="0.85" strokeLinejoin="round" />

        {strikeLabels.map((label, i) => (
          <text key={label} x={padL + (i / (strikeLabels.length - 1)) * plotW} y={H - 10} textAnchor="middle" fill="#7dd3fc" fontSize="8" fontFamily="ui-monospace, monospace">{label}</text>
        ))}
      </svg>
    </div>
  );
}

// Trimmed variant of components/SystemMonitor.tsx — same platform-capacity
// tiles, minus the fabricated "Active Sessions" live counter and the
// invented region/uptime footer.
function SystemStats() {
  const tiles = [
    { icon: IconZap, label: 'Contracts / Day', value: '3K+', color: 'emerald' },
    { icon: IconActivity, label: 'Records Processed / Day', value: '17M+', color: 'purple' },
    { icon: IconZap, label: 'Avg Data Refresh', value: '<2s', color: 'amber' },
    { icon: IconServer, label: 'System Status', value: 'ALL SYSTEMS GO', color: 'cyan' },
  ];
  const colorMap: Record<string, string> = {
    emerald: 'bg-emerald-500/20 text-emerald-400',
    purple: 'bg-purple-500/20 text-purple-400',
    amber: 'bg-amber-500/20 text-amber-400',
    cyan: 'bg-cyan-500/20 text-cyan-400',
  };
  return (
    <div className="w-full bg-[#0B0E14] border-y border-white/5 py-8">
      <div className="max-w-5xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        {tiles.map((t, i) => (
          <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/5">
            <div className={`p-3 rounded-lg ${colorMap[t.color]}`}>
              <t.icon className="w-6 h-6" />
            </div>
            <div>
              <div className="text-xs text-gray-400 uppercase font-mono tracking-wider mb-1">{t.label}</div>
              <div className="text-lg font-bold text-white font-mono">{t.value}</div>
            </div>
          </div>
        ))}
      </div>
      <p className="max-w-5xl mx-auto px-6 mt-3 text-[10px] text-gray-600 font-mono">Platform metrics, updated daily · not a per-user performance guarantee</p>
    </div>
  );
}

// Sourced from PB `apps` collection (Production_ready=true), 2026-08-09.
// Keep in sync with the collection if names/tiers change.
const PAIN_POINTS = [
  { bad: 'Watching price action and guessing where dealers will defend', good: 'Signal Engine: GEX/DEX/Vanna by strike, updated live' },
  { bad: 'Free Discord calls with no receipts when they miss', good: 'Greeks Intelligence: AI regime calls, logged hourly with reasoning' },
  { bad: 'Missing block trades until the move is already over', good: 'Institutional Tape: real-time whale sweeps, not lotto noise' },
  { bad: 'Levels that only live inside someone else’s dashboard', good: 'TradingView Daily Levels: export GEX/DEX/OI straight to your chart' },
  { bad: 'Checking SPX, ES, QQQ, NQ, SPY separately and missing when they move together', good: 'All-in-One Market Levels: every ticker\'s key levels on one screen, in sync or diverging' },
  { bad: 'Price rejects a point with no obvious reason on your one chart', good: 'Check every ticker\'s levels at once — see the confluence that explains the rejection' },
];

const FEATURES = [
  { icon: IconGauge, title: 'Signal Engine', tier: 'Level 1', desc: 'Real-time GEX/DEX/Vanna, Whale activity, and a live trade simulator — the platform’s core 0DTE dashboard.' },
  { icon: IconChart, title: 'Advanced Chart Mode', tier: 'Level 1', desc: 'Correlate any two metrics — Vol, OI, GEX, DEX, CEX, VANX, IV — on the same strike axis to see exactly where pressure builds and voids open up.' },
  { icon: IconWaves, title: 'Greeks Intelligence: 0DTE', tier: 'Level 2', desc: 'AI-driven hourly reads on Gamma Flip, Pin Points, and dealer hedging predictions for the tactical session.' },
  { icon: IconMagnet, title: 'Greeks Intelligence: Weeklies', tier: 'Level 2', desc: 'Multi-week structural view — Vanna, Charm, and Vega decay mapped across the next four expirations.' },
  { icon: IconEye, title: 'Institutional Tape', tier: 'Level 2', desc: 'Filters out lotto noise to surface real block trades and coordinated sweeps in real time.' },
  { icon: IconLayers, title: 'All-in-One Market Levels', tier: 'Level 2', desc: 'SPX/SPY/ES/QQQ/NQ aligned on one screen — see the instant any of them approaches a key level, and whether the whole market is moving in sync or diverging.' },
];

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10 py-5">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-left gap-4">
        <span className="font-semibold text-white">{q}</span>
        <IconArrowRight className={`w-4 h-4 shrink-0 text-brand-blue transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && <p className="text-sm text-gray-400 mt-3 leading-relaxed">{a}</p>}
    </div>
  );
}

export default function LandingDContent() {
  // Yearly billing isn't offered yet (no yearly Stripe prices wired up) —
  // monthly only until that's resolved. See task_7a33f2c3.
  const plans = STATIC_PLANS.filter((p) => p.interval === 'month' && p.show_pricing);

  return (
    <div className="min-h-screen bg-brand-navy text-white">
      {/* Nav */}
      <header className="sticky top-0 z-50 bg-brand-navy/90 backdrop-blur-sm border-b border-white/5">
        <div className="container mx-auto px-4 sm:px-6 py-3.5 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LogoAnimatedGreeks className="w-8 h-8" />
            <span className="font-bold">ΘDTE <span className="text-brand-orange">Toolbox</span></span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-gray-300">
            <a href="#proof" className="hover:text-white transition-colors">Why it works</a>
            <a href="#features" className="hover:text-white transition-colors">Product</a>
            <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
            <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
            <Link href="/docs" className="hover:text-white transition-colors">Docs</Link>
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            <Link href="/login" className="text-sm font-medium text-gray-300 hover:text-white transition-colors px-1 sm:px-0">
              Log in
            </Link>
            <Link href="/register" className="px-3.5 sm:px-5 py-2 sm:py-2.5 bg-brand-orange hover:bg-brand-orange/90 rounded-lg font-semibold text-sm shadow-lg shadow-brand-orange/30 transition-all whitespace-nowrap">
              Start Free
            </Link>
          </div>
        </div>
      </header>

      {/* Hero — single positioning statement, named ICP, one CTA */}
      <section className="container mx-auto px-6 pt-20 pb-16 text-center">
        <p className="text-xs uppercase tracking-[0.2em] text-brand-blue font-semibold mb-4">
          For SPX / SPY / QQQ / NQ / ES / IWM 0DTE options traders
        </p>
        <h1 className="text-4xl md:text-6xl font-bold tracking-tight leading-tight max-w-4xl mx-auto">
          Stop guessing where dealers defend.
          <span className="block text-brand-orange mt-2">See it, before the move.</span>
        </h1>
        <p className="text-lg text-gray-400 max-w-2xl mx-auto mt-6">
          Real-time GEX, DEX, CEX & Vanna dealer positioning — computed from the live options
          chain, not a lagging retail feed. Built for traders who make decisions in minutes, not days.
        </p>
        <div className="flex items-center justify-center gap-4 mt-8">
          <Link href="/register" className="px-7 py-3.5 bg-brand-orange hover:bg-brand-orange/90 rounded-xl font-bold shadow-lg shadow-brand-orange/40 hover:shadow-brand-orange/60 transition-all flex items-center gap-2">
            Start Free — No Card Required <IconArrowRight className="w-4 h-4" />
          </Link>
          <a href="#proof" className="px-7 py-3.5 border border-white/15 hover:border-white/30 hover:bg-white/5 rounded-xl font-semibold transition-all">
            See how it works
          </a>
        </div>
        <p className="text-xs text-gray-500 mt-4">Free tier: 15-min delayed · Paid tiers: live during market hours · Cancel anytime</p>

        <HeroGexSnapshot />
      </section>

      {/* Before/After — pain framed against mechanism, not adjectives */}
      <section id="proof" className="container mx-auto px-6 py-16 border-t border-white/5">
        <h2 className="text-3xl font-bold tracking-tight text-center mb-2">You already know the pain</h2>
        <p className="text-gray-400 text-center mb-10 max-w-xl mx-auto">This is what changes once you can see the data dealers see.</p>
        <div className="max-w-3xl mx-auto space-y-3">
          {PAIN_POINTS.map((p, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-xl p-4">
                <IconX className="w-4 h-4 mt-0.5 shrink-0 text-red-400" />
                <p className="text-sm text-gray-400">{p.bad}</p>
              </div>
              <div className="flex items-start gap-3 bg-brand-blue/10 border border-brand-blue/20 rounded-xl p-4">
                <IconCheck className="w-4 h-4 mt-0.5 shrink-0 text-brand-blue" />
                <p className="text-sm text-gray-200">{p.good}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Feature-benefit triplets, not a feature dump */}
      <section id="features" className="container mx-auto px-6 py-16 border-t border-white/5">
        <h2 className="text-3xl font-bold tracking-tight text-center mb-2">Many ways to see the same edge</h2>
        <p className="text-gray-400 text-center mb-10 max-w-xl mx-auto">Every view streams from the same live options-chain pipeline — no stitching together separate subscriptions.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {FEATURES.map((f, i) => (
            <div key={i} className="bg-white/5 border border-white/10 shadow-lg shadow-black/20 rounded-2xl p-6 hover:border-brand-blue/30 hover:shadow-xl transition-all">
              <div className="flex items-start justify-between mb-4">
                <f.icon className="w-8 h-8 text-brand-blue" />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-brand-orange border border-brand-orange/30 rounded-full px-2 py-0.5">{f.tier}</span>
              </div>
              <h3 className="font-bold text-lg mb-2">{f.title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TradingView export — dedicated highlight, not buried in the feature grid */}
      <section className="container mx-auto px-6 py-16 border-t border-white/5">
        <div className="max-w-4xl mx-auto bg-gradient-to-br from-brand-blue/10 to-transparent border border-brand-blue/20 rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center gap-8">
          <IconChart className="w-16 h-16 text-brand-blue shrink-0" />
          <div>
            <p className="text-xs uppercase tracking-widest text-brand-blue font-semibold mb-2">TradingView Daily Levels</p>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-3">
              Never leave your charts. Plot canonical GEX, DEX &amp; OI levels on TradingView in one click.
            </h2>
            <p className="text-gray-300 leading-relaxed">
              One click pastes every canonical level straight into a purpose-built TradingView
              indicator — no copying strikes by hand, no switching tabs. Levels show up
              color-coded on the chart you already trade from.
            </p>
          </div>
        </div>
      </section>

      {/* Social proof — real signup/visit data only, no fabricated live-session counters */}
      <section className="border-t border-white/5">
        <SystemStats />
        <div className="container mx-auto px-6 py-16">
          <h2 className="text-2xl font-bold tracking-tight text-center mb-2">Where visitors have come from</h2>
          <p className="text-gray-400 text-center mb-1 max-w-xl mx-auto">400+ traders have signed up so far, visiting from New York to Singapore.</p>
          <p className="text-xs text-gray-600 text-center mb-10 font-mono">Self-reported signup count, updated daily</p>
          <GlobalTraderMap />
        </div>
      </section>

      {/* Pricing — real data from STATIC_PLANS, no invented numbers */}
      <section id="pricing" className="container mx-auto px-6 py-16 border-t border-white/5">
        <h2 className="text-3xl font-bold tracking-tight text-center mb-2">Simple, tiered pricing</h2>
        <p className="text-gray-400 text-center mb-10 max-w-xl mx-auto">Billed monthly. Cancel anytime.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((p: Plan) => (
            <div key={p.id} className={`rounded-2xl p-6 border shadow-lg shadow-black/20 ${p.highlight ? 'border-brand-orange bg-gradient-to-b from-brand-orange/10 to-transparent' : 'border-white/10 bg-white/5'}`}>
              {p.highlight && <p className="text-xs font-bold text-brand-orange uppercase tracking-widest mb-2">Most popular</p>}
              <h3 className="font-bold text-xl">{p.name}</h3>
              <p className="text-3xl font-bold font-mono mt-2">${p.price}<span className="text-sm text-gray-400 font-sans">/{p.interval}</span></p>
              <p className="text-sm text-gray-400 mt-2">{p.description}</p>
              <ul className="mt-4 space-y-2">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                    <IconCheck className="w-4 h-4 mt-0.5 shrink-0 text-brand-blue" /> {f}
                  </li>
                ))}
              </ul>
              <Link href="/register" className={`mt-6 block text-center py-2.5 rounded-lg font-semibold transition-all ${p.highlight ? 'bg-brand-orange hover:bg-brand-orange/90' : 'bg-white/10 hover:bg-white/20'}`}>
                Get Started
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ — objection handling, not marketing copy */}
      <section id="faq" className="container mx-auto px-6 py-16 border-t border-white/5 max-w-2xl">
        <h2 className="text-3xl font-bold tracking-tight text-center mb-8">Questions</h2>
        <FAQItem q="Is this financial advice?" a="No. ΘDTE Toolbox is a data and analytics platform. All signals are informational — you make your own trading decisions." />
        <FAQItem q="How real-time is 'real-time'?" a="Signal Engine data streams from Schwab's live options chains during market hours. Free-tier data is 15-minute delayed; paid tiers get live updates." />
        <FAQItem q="Do I need to know how to code or use TradingView?" a="No setup required — everything runs in your browser dashboard. A TradingView companion extension is optional, not required." />
        <FAQItem q="Can I cancel anytime?" a="Yes, no lock-in contracts. Cancel from your account settings and you keep access through the paid period." />
      </section>

      {/* Final CTA — restates the promise, single action */}
      <section className="container mx-auto px-6 py-20 border-t border-white/5 text-center">
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">Stop reacting. Start seeing it first.</h2>
        <p className="text-gray-400 mb-8 max-w-xl mx-auto">Free tier available. No card required to start.</p>
        <Link href="/register" className="inline-flex items-center gap-2 px-8 py-4 bg-brand-orange hover:bg-brand-orange/90 rounded-xl font-bold shadow-lg shadow-brand-orange/40 hover:shadow-brand-orange/60 transition-all">
          Create Free Account <IconArrowRight className="w-4 h-4" />
        </Link>
        <p className="text-sm text-gray-500 mt-4">
          Already have an account? <Link href="/login" className="text-brand-blue hover:underline">Log in</Link>
        </p>
      </section>

      <footer className="border-t border-white/5 py-8 text-center text-xs text-gray-500">
        <p className="mb-2">
          <Link href="/docs" className="text-brand-blue hover:underline">Documentation</Link>
        </p>
        ΘDTE Toolbox — Educational analytics platform. Not financial advice.
      </footer>
    </div>
  );
}
